import { useState } from "react";
import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import {
  type BorderSide,
  type RuntimePropsWithChildren,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  toCssLength,
} from "./shared";
import { TextChild } from "./textChild";
import { IconChild } from "./iconChild";
import { ExpandableProvider } from "./expandableContext";
import { type WBReveal } from "./reveal";

export function FaQChild({
  isVisible = true,
  isVisibleQuestion,
  textQuestion,
  fontSizeQuestion = "text-sm",
  fontFamilyQuestion,
  textAlignQuestion = "left",
  fontColorQuestion,
  isVisibleAnswer,
  textAnswer,
  fontSizeAnswer = "text-sm",
  fontFamilyAnswer,
  textAlignAnswer = "left",
  fontColorAnswer,
  backgroundColor = "transparent",
  borderRadius,
  border = true,
  borderColor,
  borderWidth = "border",
  borderStyle = "border-solid",
  borderSides = [],
  padding = "p-3 wb-md:p-4 wb-lg:p-5",
  distQuestionAnswer = "gap-2",
  distIconText = "gap-4",
  distTitleContent = "mt-2",
  distExpandedText = "mb-2",
  isExpandable = true,
  alwaysOpen = false,
}: {
  isVisible?: boolean;
  isVisibleQuestion?: boolean;
  textQuestion?: string;
  fontSizeQuestion?: string;
  fontFamilyQuestion?: string;
  textAlignQuestion?: "left" | "center" | "right" | "justify";
  fontColorQuestion?: string;
  isVisibleAnswer?: boolean;
  textAnswer?: string;
  fontSizeAnswer?: string;
  fontFamilyAnswer?: string;
  textAlignAnswer?: "left" | "center" | "right" | "justify";
  fontColorAnswer?: string;
  backgroundColor?: string;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  padding?: string;
  distQuestionAnswer?: string;
  distIconText?: string;
  distTitleContent?: string;
  distExpandedText?: string;
  isExpandable?: boolean;
  alwaysOpen?: boolean;
}) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  const [isOpen, setIsOpen] = useState(false);
  if (!isVisible) return null;
  const showExpanded = isExpandable && (alwaysOpen || isOpen);
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  return (
    <div
      className={classNames(
        "flex flex-col",
        padding,
        borderClasses,
        roundedClasses,
      )}
      style={{
        transition: "all 300ms ease-in-out",
        backgroundColor:
          backgroundColor !== "transparent" ? backgroundColor : undefined,
        borderColor: borderColor ?? currentColorPalette.secondary,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      {isExpandable && !alwaysOpen ? (
        <div
          className={classNames(
            "w-full flex items-center justify-between",
            distIconText,
          )}
          aria-expanded={isOpen}
        >
          <div className="grow min-w-0">
            <TextChild
              isVisible={isVisibleQuestion}
              text={textQuestion}
              fontSize={fontSizeQuestion}
              fontFamily={fontFamilyQuestion}
              textAlign={textAlignQuestion}
              color={fontColorQuestion}
              headingLevel="h2"
            />
          </div>
          <div
            className="shrink-0 cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => setIsOpen((prev) => !prev)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ")
                setIsOpen((prev) => !prev);
            }}
          >
            <ExpandableProvider value={{ isExpanded: showExpanded }}>
              <Element
                is={IconChild}
                id="icon"
                iconName="IconPlus"
                iconColor={undefined}
                iconSize="medium"
                iconAnimationMode="rotate"
                expandedIconName="IconMinus"
              />
            </ExpandableProvider>
          </div>
        </div>
      ) : (
        <div
          className={classNames(
            "w-full",
            !isExpandable ? distQuestionAnswer : undefined,
          )}
        >
          <TextChild
            isVisible={isVisibleQuestion}
            text={textQuestion}
            fontSize={fontSizeQuestion}
            fontFamily={fontFamilyQuestion}
            textAlign={textAlignQuestion}
            color={fontColorQuestion ?? currentColorPalette.primary}
            headingLevel="h2"
          />
        </div>
      )}
      {isExpandable ? (
        <motion.div
          className={classNames(distTitleContent, distExpandedText)}
          initial={false}
          animate={{
            height: showExpanded ? "auto" : 0,
            opacity: showExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          style={{ overflow: "hidden" }}
        >
          <TextChild
            isVisible={isVisibleAnswer}
            text={textAnswer}
            fontSize={fontSizeAnswer}
            fontFamily={fontFamilyAnswer}
            textAlign={textAlignAnswer}
            color={fontColorAnswer}
          />
        </motion.div>
      ) : null}
      {!isExpandable ? (
        <div>
          <TextChild
            isVisible={isVisibleAnswer}
            text={textAnswer}
            fontSize={fontSizeAnswer}
            fontFamily={fontFamilyAnswer}
            textAlign={textAlignAnswer}
            color={fontColorAnswer}
          />
        </div>
      ) : null}
    </div>
  );
}

export function FaQ(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextEntries =
    (props.distTextEntries as string | undefined) ?? "gap-3";
  const distEntries = (props.distEntries as string | undefined) ?? "space-y-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "100%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 w-full flex flex-col",
          alignmentClass,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distTextEntries,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              "flex flex-col grow justify-center",
              leftWidth,
            )}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <Element
              is={TextChild}
              id="text"
              isVisible={props.isVisibleText as boolean | undefined}
              text={props.textText as string | undefined}
              fontSize={props.fontSizeText as string | undefined}
              textAlign={
                props.textAlignText as
                  | "left"
                  | "center"
                  | "right"
                  | "justify"
                  | undefined
              }
            />
          </div>
          <div className={classNames("flex flex-col", rightWidth, distEntries)}>
            {props.children}
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
